import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import AdminBroadcasts from "@/components/AdminBroadcasts";
import TrialEmailsPanel from "@/components/TrialEmailsPanel";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Activity, BarChart3, Eye, ShieldCheck, ArrowUpRight, Trash2, Mountain, Sparkles, Megaphone, Send, Mail, GraduationCap, RefreshCw, FileText } from "lucide-react";

const TABS = [
  { key: "stats", label: "Overview", icon: BarChart3 },
  { key: "users", label: "Users", icon: Users },
  { key: "climbers", label: "Climbers", icon: Mountain },
  { key: "teachers", label: "Teachers", icon: GraduationCap },
  { key: "purchase-orders", label: "Purchase orders", icon: FileText },
  { key: "broadcasts", label: "Broadcasts", icon: Megaphone },
  { key: "trial-emails", label: "Trial emails", icon: Mail },
  { key: "activity", label: "Activity", icon: Activity },
];

const INTEREST_LABELS = {
  interest_activity: "Activity",
  interest_hobby: "Hobby",
  interest_subject: "Subject",
  interest_career: "Career",
  interest_explore: "Explore",
};

const Admin = () => {
  const { user, refreshUser, loading } = useAuth();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [teacherStats, setTeacherStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState(null);
  const [flagged, setFlagged] = useState([]);
  const [climbers, setClimbers] = useState([]);
  const [climbersLoaded, setClimbersLoaded] = useState(false);
  const [pending, setPending] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null); // user object pending deletion
  const [removing, setRemoving] = useState(false);
  const [teachersList, setTeachersList] = useState([]);
  const [teacherDrill, setTeacherDrill] = useState(null); // {teacher, classes} when a row is expanded
  const [teacherDrillId, setTeacherDrillId] = useState(null);
  const [togglingRole, setTogglingRole] = useState(false);
  const [grantDialog, setGrantDialog] = useState(null);  // { user, kind: "summit"|"teacher" }
  const [grantMonths, setGrantMonths] = useState(12);
  const [grantBusy, setGrantBusy] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [poSummary, setPoSummary] = useState(null);

  const loadPurchaseOrders = async () => {
    try {
      const r = await api.get("/admin/purchase-orders");
      setPurchaseOrders(r.data.purchase_orders || []);
      setPoSummary(r.data.summary || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load POs");
    }
  };

  const updatePoStatus = async (po, next) => {
    try {
      await api.put(`/admin/purchase-orders/${po.id}`, { status: next });
      toast.success(`Status → ${next}`);
      await loadPurchaseOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update");
    }
  };

  const savePoField = async (po, field, value) => {
    try {
      await api.put(`/admin/purchase-orders/${po.id}`, { [field]: value });
      await loadPurchaseOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  const activatePo = async (po) => {
    if (!window.confirm(`Provision ${po.accounts_requested} teacher accounts for ${po.school_name} and email the admin?`)) return;
    try {
      const r = await api.post(`/admin/purchase-orders/${po.id}/activate`);
      toast.success(`Activated ${r.data.activated_count} teacher accounts`);
      await loadPurchaseOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Activation failed");
    }
  };

  const submitGrant = async () => {
    if (!grantDialog) return;
    setGrantBusy(true);
    try {
      const path = grantDialog.kind === "summit" ? "grant-summit-pass" : "grant-teacher-trail";
      const r = await api.post(`/admin/users/${grantDialog.user.id}/${path}`, { months: parseInt(grantMonths) || 0 });
      toast.success(`${grantDialog.kind === "summit" ? "Summit Pass" : "Teacher Trail"} granted to ${grantDialog.user.email}`);
      setGrantDialog(null);
      setGrantMonths(12);
      await refreshUsers();
      if (tab === "teachers") await loadTeachers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Grant failed");
    } finally {
      setGrantBusy(false);
    }
  };

  const grantSeats = async (teacher, delta = 30) => {
    try {
      const r = await api.post(`/admin/users/${teacher.id}/grant-teacher-seats`, { increment: delta });
      toast.success(`${teacher.email} now has ${r.data.effective_cap} total seats (${r.data.bonus_student_slots} bonus)`);
      await loadTeachers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not grant seats");
    }
  };

  const toggleMyTeacherMode = async () => {
    setTogglingRole(true);
    try {
      const r = await api.post("/admin/toggle-my-teacher-role", {});
      toast.success(r.data.role === "teacher" ? "You are now a teacher — visit Classes" : "Teacher mode off");
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not toggle role");
    } finally {
      setTogglingRole(false);
    }
  };

  const loadTeachers = async () => {
    try {
      const r = await api.get("/admin/teachers");
      setTeachersList(r.data.teachers || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load teachers");
    }
  };

  const openTeacherDrill = async (teacher) => {
    if (teacherDrillId === teacher.id) { setTeacherDrillId(null); setTeacherDrill(null); return; }
    setTeacherDrillId(teacher.id);
    try {
      const r = await api.get(`/admin/teachers/${teacher.id}/classes`);
      setTeacherDrill(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load teacher classes");
    }
  };

  useEffect(() => {
    if (tab === "teachers") loadTeachers();
    if (tab === "purchase-orders") loadPurchaseOrders();
    // eslint-disable-next-line
  }, [tab]);

  useEffect(() => {
    if (!user?.is_admin) return;
    const load = async () => {
      try {
        const [s, u, a, f, t] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/users"),
          api.get("/admin/activity"),
          api.get("/admin/flagged-inputs?limit=100"),
          api.get("/admin/teacher-stats").catch(() => ({ data: null })),
        ]);
        setStats(s.data); setUsers(u.data); setActivity(a.data); setFlagged(f.data); setTeacherStats(t.data);
      } catch (err) {
        toast.error("Could not load admin data");
      }
    };
    load();
  }, [user?.is_admin]);

  const refreshUsers = async () => {
    try {
      const [u, s] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/stats"),
      ]);
      setUsers(u.data);
      setStats(s.data);
    } catch (err) {
      console.error("Failed to refresh users after purge:", err);
    }
  };

  // Lazy-load climbers only when the tab is opened the first time.
  useEffect(() => {
    if (tab !== "climbers" || climbersLoaded || !user?.is_admin) return;
    api.get("/admin/students").then((r) => {
      setClimbers(r.data || []);
      setClimbersLoaded(true);
    }).catch(() => toast.error("Could not load climbers"));
  }, [tab, climbersLoaded, user?.is_admin]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-mm-navy/60">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  const setViewAs = async (plan) => {
    setPending(true);
    try {
      await api.post("/admin/view-as", { plan });
      await refreshUser();
      toast.success(plan ? `Previewing as ${plan} user` : "View-As cleared (admin default)");
    } catch (err) {
      toast.error("Could not set view-as");
    } finally {
      setPending(false);
    }
  };

  const removeUser = async () => {
    if (!confirmRemove) return;
    setRemoving(true);
    try {
      const res = await api.delete(`/admin/users/${confirmRemove.id}`);
      setUsers((list) => list.filter((u) => u.id !== confirmRemove.id));
      const c = res.data?.counts || {};
      toast.success(
        `Removed ${confirmRemove.email}. Cleaned ${c.students_deleted || 0} climbers, ${c.expeditions_deleted || 0} expeditions.`
      );
      setConfirmRemove(null);
      // Refresh stats so counters reflect the deletion
      try {
        const s = await api.get("/admin/stats");
        setStats(s.data);
      } catch (err) {
        // Non-fatal: the deletion succeeded; only the stat refresh failed.
        console.error("Stats refresh after user delete failed:", err);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove user");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo">
      <Navbar />
      <div className="container mx-auto py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel flex items-center gap-2">
              <ShieldCheck size={14} strokeWidth={2.5} /> Mountain HQ — Admin
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black text-mm-navy mt-2" data-testid="admin-title">
              Expedition Command
            </h1>
            <p className="text-mm-navy/70 mt-2">Full access. Inspect users, subscriptions, and app activity.</p>
          </div>
          <div className="flex gap-2 flex-wrap items-end">
            <button
              onClick={toggleMyTeacherMode}
              disabled={togglingRole}
              className={`btn-trail text-sm disabled:opacity-50 ${user.role === "teacher" ? "btn-gold" : "btn-outline-navy"}`}
              data-testid="admin-toggle-teacher-mode-btn"
              title="Flip your own account between parent and teacher"
            >
              <GraduationCap size={14} strokeWidth={2.5} />
              {user.role === "teacher" ? "Teacher mode: ON" : "Enable teacher mode"}
            </button>
            {user.role === "teacher" && (
              <Link to="/teacher/dashboard" className="btn-trail btn-gold text-sm" data-testid="admin-go-to-classes">
                My classes →
              </Link>
            )}
            <Link to="/dashboard" className="btn-trail btn-outline-navy text-sm">Back to my dashboard →</Link>
          </div>
        </div>

        {/* View-As toggle */}
        <div className="mt-8 bg-white border-2 border-mm-gold/40 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="view-as-panel">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-mm-gold/20 text-mm-navy flex items-center justify-center shrink-0">
              <Eye size={20} strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-heading font-bold text-mm-navy">Test mode — preview as</div>
              <div className="text-xs text-mm-navy/60 mt-0.5">
                You're currently viewing as <strong className="text-mm-navy">{user.effective_plan}</strong>
                {user.view_as_plan ? " (override active)" : " (admin default)"}. Free-tier limits apply when set to free.
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <ViewAsBtn active={!user.view_as_plan} onClick={() => setViewAs(null)} disabled={pending} testid="view-as-admin">Admin</ViewAsBtn>
            <ViewAsBtn active={user.view_as_plan === "free"} onClick={() => setViewAs("free")} disabled={pending} testid="view-as-free">Free user</ViewAsBtn>
            <ViewAsBtn active={user.view_as_plan === "premium"} onClick={() => setViewAs("premium")} disabled={pending} testid="view-as-premium">Premium user</ViewAsBtn>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-8 flex gap-2 border-b border-mm-navy/10">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-3 font-heading font-bold text-sm flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                  tab === t.key ? "border-mm-gold text-mm-navy" : "border-transparent text-mm-navy/50 hover:text-mm-navy"
                }`}
                data-testid={`admin-tab-${t.key}`}
              >
                <Icon size={16} strokeWidth={2.5} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Stats */}
        {tab === "stats" && stats && (
          <>
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats-grid">
              <StatBox label="Total users" value={stats.total_users} />
              <StatBox label="Premium" value={stats.premium_users} accent />
              <StatBox label="On trial" value={stats.trial_users} />
              <StatBox label="Free" value={stats.free_users} />
              <StatBox label="Climbers" value={stats.total_students} />
              <StatBox label="Total expeditions" value={stats.total_expeditions} />
              <StatBox label="Active" value={stats.active_expeditions} />
              <StatBox label="Completed" value={stats.completed_expeditions} />
              <StatBox label="Paid txns" value={stats.paid_transactions} />
              <StatBox label="Pending txns" value={stats.pending_transactions} />
              <StatBox label="Revenue (USD)" value={`$${(stats.total_revenue_usd ?? 0).toFixed(2)}`} accent />
              <StatBox label="Admins" value={stats.admin_count} />
            </div>
            {teacherStats && (
              <div className="mt-8" data-testid="admin-teacher-stats">
                <h3 className="font-heading text-xl font-black text-mm-navy">Teacher platform</h3>
                <p className="text-xs text-mm-navy/60 mt-1">Aggregate counts only — no individual anonymous student data is ever shown.</p>
                <div className="mt-3 grid grid-cols-2 lg:grid-cols-5 gap-4">
                  <StatBox label="Teachers" value={teacherStats.total_teachers} />
                  <StatBox label="Paid teachers" value={teacherStats.paid_teachers} accent />
                  <StatBox label="Classes" value={teacherStats.total_classes} />
                  <StatBox label="Anonymous students" value={teacherStats.total_anonymous_students} />
                  <StatBox label="Personalization quizzes" value={teacherStats.personalization_quizzes_completed} />
                </div>
              </div>
            )}
          </>
        )}

        {/* Users */}
        {tab === "users" && (
          <>
            <PurgeTestAccountsPanel onPurged={refreshUsers} />
            <div className="mt-6 overflow-x-auto bg-white border border-mm-navy/10 rounded-2xl" data-testid="admin-users-table">
            <table className="w-full text-sm">
              <thead className="bg-mm-parchment text-mm-navy">
                <tr className="text-left">
                  <Th>User</Th><Th>Plan</Th><Th>Trial status</Th><Th>Trial ends</Th><Th>Converted</Th><Th>Subscription</Th><Th>Climbers</Th><Th>Expeditions</Th><Th>Flags</Th><Th>Joined</Th><Th>Role</Th><Th>Terms</Th><Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-mm-navy/10 hover:bg-mm-parchment/40" data-testid={`admin-user-row-${u.id}`}>
                    <Td>
                      <div className="font-bold text-mm-navy">{u.name}</div>
                      <div className="text-xs text-mm-navy/60">{u.email}</div>
                    </Td>
                    <Td><PlanPill plan={u.plan} /></Td>
                    <Td>
                      {u.trial_status === "active" && <span className="px-2 py-0.5 rounded-full bg-mm-gold/20 text-mm-navy text-xs font-bold uppercase tracking-widest">Active · {u.trial_days_remaining}d</span>}
                      {u.trial_status === "expired" && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold uppercase tracking-widest">Expired</span>}
                      {u.trial_status === "converted" && <span className="px-2 py-0.5 rounded-full bg-mm-steel/20 text-mm-navy text-xs font-bold uppercase tracking-widest">Converted</span>}
                      {u.trial_status === "n/a" && <span className="text-xs text-mm-navy/40">—</span>}
                    </Td>
                    <Td className="text-xs text-mm-navy/70">{u.trial_ends_at?.slice(0, 10) || "—"}</Td>
                    <Td>{u.converted_to_paid ? <span className="text-mm-steel font-bold">✓</span> : <span className="text-mm-navy/40">—</span>}</Td>
                    <Td>
                      <div className="text-mm-navy/80">{u.subscription_status || "—"}</div>
                      <div className="text-xs text-mm-navy/50">{u.premium_plan ? `${u.premium_plan} • ${u.stripe_customer_id ? "Stripe" : "—"}` : ""}</div>
                    </Td>
                    <Td>{u.student_count}</Td>
                    <Td>{u.expedition_count}</Td>
                    <Td>
                      {u.flag_count > 0
                        ? <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold">{u.flag_count}</span>
                        : <span className="text-xs text-mm-navy/40">—</span>}
                    </Td>
                    <Td className="text-xs text-mm-navy/60">{u.created_at?.slice(0, 10)}</Td>
                    <Td>
                      {u.is_admin && <span className="px-2 py-0.5 rounded-full bg-mm-gold/25 text-mm-navy text-xs font-bold uppercase tracking-widest">Admin</span>}
                      {!u.is_admin && <span className="text-xs text-mm-navy/60">{u.role}</span>}
                    </Td>
                    <Td>
                      {u.terms_accepted ? (
                        <div className="flex flex-col leading-tight" data-testid={`admin-terms-cell-${u.id}`}>
                          <span className="text-emerald-700 font-bold text-xs">✓ Accepted</span>
                          <span className="text-[10px] text-mm-navy/55">
                            v{u.terms_version || "—"}
                            {u.terms_accepted_date && (
                              <> · {new Date(u.terms_accepted_date).toLocaleDateString()}</>
                            )}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-amber-700 font-bold" data-testid={`admin-terms-cell-${u.id}`}>Pending</span>
                      )}
                    </Td>
                    <Td>
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => setConfirmRemove(u)}
                          disabled={u.id === user.id}
                          title={u.id === user.id ? "You can't remove your own account" : "Remove user"}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed text-xs font-bold transition-colors"
                          data-testid={`admin-remove-user-btn-${u.id}`}
                        >
                          <Trash2 size={14} strokeWidth={2.5} /> Remove
                        </button>
                        <button
                          onClick={() => setGrantDialog({ user: u, kind: "summit" })}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-mm-gold/40 text-mm-navy hover:bg-mm-gold/10 text-xs font-bold transition-colors"
                          data-testid={`admin-grant-summit-btn-${u.id}`}
                          title="Comp Summit Pass — no charge"
                        >
                          <Sparkles size={12} strokeWidth={2.5} /> Grant Summit
                        </button>
                        <button
                          onClick={() => setGrantDialog({ user: u, kind: "teacher" })}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-mm-navy/20 text-mm-navy hover:bg-mm-navy/5 text-xs font-bold transition-colors"
                          data-testid={`admin-grant-teacher-btn-${u.id}`}
                          title="Comp Teacher Trail — no charge"
                        >
                          <GraduationCap size={12} strokeWidth={2.5} /> Grant Teacher
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
                {!users.length && (
                  <tr><Td colSpan={13}><span className="text-mm-navy/60">No users yet.</span></Td></tr>
                )}
              </tbody>
            </table>
          </div>
          </>
        )}

        {/* Climbers (all student profiles with interests) */}
        {tab === "climbers" && (
          <div className="mt-8" data-testid="admin-climbers-tab">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading text-xl font-bold text-mm-navy">All climbers</h2>
                <p className="text-sm text-mm-navy/60">
                  Every climber across the platform with their personalization quiz answers.
                </p>
              </div>
              <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">
                {climbers.length} climber{climbers.length === 1 ? "" : "s"}
              </div>
            </div>
            <div className="overflow-x-auto bg-white border border-mm-navy/10 rounded-2xl" data-testid="admin-climbers-table">
              <table className="w-full text-sm">
                <thead className="bg-mm-parchment text-mm-navy">
                  <tr className="text-left">
                    <Th>Climber</Th><Th>Grade</Th><Th>Parent</Th><Th>Interests</Th><Th>Joined</Th>
                  </tr>
                </thead>
                <tbody>
                  {climbers.map((c) => {
                    const filled = Object.keys(INTEREST_LABELS).filter((k) => (c?.[k] || "").trim().length > 0);
                    return (
                      <tr key={c.id} className="border-t border-mm-navy/10 align-top" data-testid={`admin-climber-row-${c.id}`}>
                        <Td>
                          <div className="font-bold text-mm-navy">{c.name}</div>
                          <div className="text-[10px] uppercase tracking-widest text-mm-steel font-bold mt-0.5">
                            AP {c.altitude_points || 0} · {c.compass_streak || 0}d streak
                          </div>
                        </Td>
                        <Td className="text-mm-navy/80">{c.grade || "—"}</Td>
                        <Td>
                          <div className="text-mm-navy/85">{c.parent_name || "—"}</div>
                          <div className="text-xs text-mm-navy/55">{c.parent_email || "—"}</div>
                        </Td>
                        <Td className="max-w-xl">
                          {filled.length === 0 ? (
                            <span className="text-xs text-mm-navy/40 italic" data-testid={`admin-climber-no-interests-${c.id}`}>
                              No personalization quiz yet
                            </span>
                          ) : (
                            <div className="flex flex-col gap-1.5">
                              <div className="text-[10px] uppercase tracking-widest font-bold text-mm-steel flex items-center gap-1.5">
                                <Sparkles size={11} strokeWidth={2.5} /> {filled.length}/5 answered
                              </div>
                              <ul className="space-y-1" data-testid={`admin-climber-interests-${c.id}`}>
                                {filled.map((k) => (
                                  <li key={k} className="text-mm-navy/85 leading-snug">
                                    <span className="text-[10px] uppercase tracking-widest font-bold text-mm-steel mr-2">
                                      {INTEREST_LABELS[k]}
                                    </span>
                                    <span className="break-words">{c[k]}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </Td>
                        <Td className="text-xs text-mm-navy/60 whitespace-nowrap">{c.created_at?.slice(0, 10) || "—"}</Td>
                      </tr>
                    );
                  })}
                  {climbersLoaded && !climbers.length && (
                    <tr><Td colSpan={5}><span className="text-mm-navy/60">No climbers yet.</span></Td></tr>
                  )}
                  {!climbersLoaded && (
                    <tr><Td colSpan={5}><span className="text-mm-navy/60">Loading climbers…</span></Td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Broadcasts */}
        {tab === "broadcasts" && <AdminBroadcasts />}

        {/* Teachers drilldown — aggregate metrics only, never individual anon student data */}
        {tab === "teachers" && (
          <div className="mt-8" data-testid="admin-teachers-tab">
            <div className="rounded-xl border-2 border-mm-steel/20 bg-white p-4 text-sm text-mm-navy/85 mb-4">
              <ShieldCheck size={14} className="inline mr-1 text-mm-gold" strokeWidth={2.5} />
              Privacy: this view shows aggregate metrics per teacher & per class only. Individual anonymous student accounts are never listed.
            </div>
            <div className="overflow-x-auto bg-white border border-mm-navy/10 rounded-2xl">
              <table className="w-full text-sm" data-testid="admin-teachers-table">
                <thead className="bg-mm-parchment text-mm-navy">
                  <tr className="text-left">
                    <Th>Teacher</Th><Th>Plan</Th><Th>Classes</Th><Th>Anon students</Th><Th>Bonus seats</Th><Th>Expeditions</Th><Th>Joined</Th><Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {teachersList.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-8 text-mm-navy/50">No teachers yet.</td></tr>
                  )}
                  {teachersList.map((t) => (
                    <>
                      <tr key={t.id} className="border-t border-mm-navy/10 hover:bg-mm-parchment/40" data-testid={`admin-teacher-row-${t.id}`}>
                        <Td>
                          <div className="font-bold text-mm-navy">{t.name}</div>
                          <div className="text-xs text-mm-navy/60">{t.email}</div>
                        </Td>
                        <Td>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-widest ${t.teacher_unlocked ? "bg-mm-gold/20 text-mm-navy" : "bg-mm-navy/10 text-mm-navy/70"}`}>
                            {t.teacher_unlocked ? "Premium" : "Free"}
                          </span>
                        </Td>
                        <Td>{t.classes_count}</Td>
                        <Td>
                          <div className="font-bold text-mm-navy">{t.total_students}</div>
                          <div className="text-[10px] text-mm-navy/55">of {t.effective_cap || 30} cap</div>
                        </Td>
                        <Td>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-mm-navy">+{t.bonus_student_slots || 0}</span>
                            <div className="flex gap-1">
                              <button
                                onClick={() => grantSeats(t, 30)}
                                className="px-2 py-0.5 rounded border border-mm-gold/40 text-mm-navy hover:bg-mm-gold/10 text-[10px] font-bold"
                                data-testid={`admin-add-seats-btn-${t.id}`}
                                title="Add 30 seats"
                              >
                                +30
                              </button>
                              {(t.bonus_student_slots || 0) > 0 && (
                                <button
                                  onClick={() => grantSeats(t, -30)}
                                  className="px-2 py-0.5 rounded border border-mm-navy/20 text-mm-navy hover:bg-mm-navy/5 text-[10px] font-bold"
                                  data-testid={`admin-remove-seats-btn-${t.id}`}
                                  title="Remove 30 seats"
                                >
                                  −30
                                </button>
                              )}
                            </div>
                          </div>
                        </Td>
                        <Td>{t.total_expeditions}</Td>
                        <Td className="text-xs text-mm-navy/60">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</Td>
                        <Td>
                          <button
                            onClick={() => openTeacherDrill(t)}
                            className="text-mm-steel hover:text-mm-navy font-bold text-xs"
                            data-testid={`admin-teacher-drill-${t.id}`}
                          >
                            {teacherDrillId === t.id ? "Close" : "Classes →"}
                          </button>
                        </Td>
                      </tr>
                      {teacherDrillId === t.id && teacherDrill && (
                        <tr className="bg-mm-parchment/50">
                          <td colSpan={8} className="px-6 py-4" data-testid={`admin-teacher-drilldown-${t.id}`}>
                            <div className="text-xs uppercase tracking-widest font-bold text-mm-steel mb-2">
                              {teacherDrill.classes.length} classes for {teacherDrill.teacher.name}
                            </div>
                            {teacherDrill.classes.length === 0 ? (
                              <div className="text-sm text-mm-navy/60">This teacher hasn't created any classes yet.</div>
                            ) : (
                              <table className="w-full text-xs">
                                <thead className="text-mm-steel">
                                  <tr><th className="text-left py-1">Class</th><th className="text-left py-1">Subject</th><th className="text-left py-1">Grade</th><th className="text-left py-1">Slots</th><th className="text-left py-1">Assigned</th><th className="text-left py-1">Active exp</th><th className="text-left py-1">Completed exp</th><th className="text-left py-1">Created</th></tr>
                                </thead>
                                <tbody>
                                  {teacherDrill.classes.map((c) => (
                                    <tr key={c.id} className="border-t border-mm-navy/5">
                                      <td className="py-1.5 font-bold text-mm-navy">{c.name}</td>
                                      <td className="py-1.5">{c.subject || "—"}</td>
                                      <td className="py-1.5">{c.grade_band || "—"}</td>
                                      <td className="py-1.5">{c.student_count}</td>
                                      <td className="py-1.5">{c.assigned_count}</td>
                                      <td className="py-1.5">{c.active_expeditions}</td>
                                      <td className="py-1.5">{c.completed_expeditions}</td>
                                      <td className="py-1.5 text-mm-navy/60">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Purchase Orders */}
        {tab === "purchase-orders" && (
          <div className="mt-8" data-testid="admin-po-tab">
            {poSummary && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="admin-po-summary">
                <StatBox label="Pending revenue" value={`$${((poSummary.pending_revenue_cents || 0) / 100).toLocaleString()}`} />
                <StatBox label="Received revenue" value={`$${((poSummary.received_revenue_cents || 0) / 100).toLocaleString()}`} accent />
                <StatBox label="Accounts activated" value={poSummary.activated_accounts || 0} />
                <StatBox label="Total POs" value={poSummary.total_pos || 0} />
              </div>
            )}
            <div className="overflow-x-auto bg-white border border-mm-navy/10 rounded-2xl">
              <table className="w-full text-sm" data-testid="admin-po-table">
                <thead className="bg-mm-parchment text-mm-navy">
                  <tr className="text-left">
                    <Th>School</Th><Th>Admin</Th><Th>Accts</Th><Th>Total</Th><Th>PO #</Th><Th>Invoice #</Th><Th>Submitted</Th><Th>Status</Th><Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseOrders.length === 0 && (
                    <tr><td colSpan={9} className="text-center py-8 text-mm-navy/50">No purchase orders yet.</td></tr>
                  )}
                  {purchaseOrders.map((po) => (
                    <tr key={po.id} className="border-t border-mm-navy/10" data-testid={`admin-po-row-${po.id}`}>
                      <Td>
                        <div className="font-bold text-mm-navy">{po.school_name}</div>
                        <div className="text-xs text-mm-navy/60">{po.district || "—"}</div>
                      </Td>
                      <Td>
                        <div className="text-mm-navy">{po.admin_name}</div>
                        <div className="text-xs text-mm-navy/60">{po.admin_email}</div>
                      </Td>
                      <Td>{po.accounts_requested}</Td>
                      <Td className="font-bold text-mm-navy">${((po.total_cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Td>
                      <Td className="text-xs">{po.po_number || "—"}</Td>
                      <Td>
                        <input
                          defaultValue={po.invoice_number || ""}
                          onBlur={(e) => e.target.value !== (po.invoice_number || "") && savePoField(po, "invoice_number", e.target.value)}
                          placeholder="—"
                          className="w-24 border border-mm-navy/15 rounded px-2 py-1 text-xs"
                          data-testid={`admin-po-invoice-input-${po.id}`}
                        />
                      </Td>
                      <Td className="text-xs text-mm-navy/60">{po.created_at ? new Date(po.created_at).toLocaleDateString() : "—"}</Td>
                      <Td>
                        <select
                          value={po.status}
                          onChange={(e) => updatePoStatus(po, e.target.value)}
                          className={`text-xs font-bold rounded-full px-2 py-1 border-2 ${
                            po.status === "activated" ? "bg-emerald-50 border-emerald-300 text-emerald-800" :
                            po.status === "paid" ? "bg-mm-gold/20 border-mm-gold/40 text-mm-navy" :
                            po.status === "canceled" ? "bg-red-50 border-red-200 text-red-700" :
                            "bg-mm-navy/5 border-mm-navy/20 text-mm-navy"
                          }`}
                          data-testid={`admin-po-status-${po.id}`}
                        >
                          {["pending","invoiced","paid","activated","canceled"].map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </Td>
                      <Td>
                        {po.status === "paid" && (
                          <button
                            onClick={() => activatePo(po)}
                            className="text-xs font-bold px-3 py-1 rounded-lg btn-trail btn-gold"
                            data-testid={`admin-po-activate-btn-${po.id}`}
                          >
                            Activate Accounts
                          </button>
                        )}
                        {po.status === "activated" && (
                          <span className="text-xs text-emerald-700 font-bold">✓ {po.activated_teacher_user_ids?.length || 0} accts</span>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Trial emails */}
        {tab === "trial-emails" && <TrialEmailsPanel />}

        {/* Activity */}
        {tab === "activity" && activity && (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="admin-activity-grid">
            <ActivityList title="Recent signups" items={activity.recent_users.map((u) => ({
              key: u.id,
              primary: u.name,
              secondary: u.email,
              meta: u.plan + (u.is_admin ? " • admin" : ""),
              when: u.created_at,
            }))} />
            <ActivityList title="Recent expeditions" items={activity.recent_expeditions.map((e) => ({
              key: e.id,
              primary: e.title || e.topic,
              secondary: `${e.difficulty} • ${e.status}`,
              meta: `${(e.completed_stages || []).length}/${(e.stages || 5)} stages`,
              when: e.created_at,
            }))} />
            <ActivityList title="Recent payments" items={activity.recent_payments.map((p) => ({
              key: p.session_id || p.id,
              primary: `${p.plan?.toUpperCase()} — $${(p.amount ?? 0).toFixed(2)}`,
              secondary: p.payment_status,
              meta: p.mode || "subscription",
              when: p.created_at,
            }))} />
          </div>
        )}

        {/* Safety */}
        {tab === "safety" && (
          <div className="mt-8" data-testid="admin-safety-tab">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading text-xl font-bold text-mm-navy">Flagged inputs</h2>
                <p className="text-sm text-mm-navy/60">Recent attempts blocked by the input or output safety filter.</p>
              </div>
              <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">
                {flagged.length} event{flagged.length === 1 ? "" : "s"} recorded
              </div>
            </div>
            <div className="overflow-x-auto bg-white border border-mm-navy/10 rounded-2xl">
              <table className="w-full text-sm">
                <thead className="bg-mm-parchment text-mm-navy">
                  <tr className="text-left">
                    <Th>When</Th><Th>User</Th><Th>Context</Th><Th>Stage</Th><Th>Original input</Th><Th>Matched</Th>
                  </tr>
                </thead>
                <tbody>
                  {flagged.map((f) => (
                    <tr key={f.id} className="border-t border-mm-navy/10" data-testid={`flagged-row-${f.id}`}>
                      <Td className="text-xs text-mm-navy/60 whitespace-nowrap">{f.created_at?.slice(0, 16).replace("T", " ")}</Td>
                      <Td>
                        <div className="font-bold text-mm-navy">{f.user_name || "—"}</div>
                        <div className="text-xs text-mm-navy/60">{f.user_email}</div>
                        {f.user_flag_count > 0 && (
                          <div className="text-[10px] mt-1 font-bold uppercase tracking-widest text-red-600">
                            {f.user_flag_count} total flag{f.user_flag_count === 1 ? "" : "s"}
                          </div>
                        )}
                      </Td>
                      <Td><span className="text-mm-navy/80">{f.context}</span></Td>
                      <Td>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-widest ${f.stage === "input_filter" ? "bg-mm-steel/15 text-mm-navy" : "bg-red-100 text-red-700"}`}>
                          {f.stage}
                        </span>
                      </Td>
                      <Td className="max-w-md"><div className="text-mm-navy/85 break-words">{f.original_text}</div></Td>
                      <Td><code className="text-xs bg-mm-navy/5 px-2 py-0.5 rounded text-red-700">{f.matched_term}</code></Td>
                    </tr>
                  ))}
                  {!flagged.length && (
                    <tr><Td colSpan={6}><span className="text-mm-navy/60">Nothing flagged. Trail's all clear.</span></Td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Admin grant dialog — Summit Pass or Teacher Trail comp */}
      {grantDialog && (
        <div
          className="fixed inset-0 z-50 bg-mm-navy/60 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => !grantBusy && setGrantDialog(null)}
          data-testid="admin-grant-dialog"
        >
          <div className="bg-white rounded-2xl max-w-md w-full p-6 border-2 border-mm-gold/40" onClick={(e) => e.stopPropagation()}>
            <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">Complimentary grant</div>
            <h3 className="font-heading text-xl font-black text-mm-navy mt-1">
              Grant {grantDialog.kind === "summit" ? "Summit Pass" : "Teacher Trail"} to {grantDialog.user.email}
            </h3>
            <p className="text-sm text-mm-navy/70 mt-2">
              No Stripe charge. {grantDialog.kind === "summit"
                ? "Sets premium plan and clears trial-expired for the chosen duration."
                : "Flips role to Teacher and unlocks unlimited classes for the chosen duration."}
            </p>
            <div className="mt-4">
              <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">Duration (months)</label>
              <div className="mt-2 flex gap-2 flex-wrap">
                {[1, 3, 6, 12, 24].map((m) => (
                  <button
                    key={m}
                    onClick={() => setGrantMonths(m)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-bold border-2 ${grantMonths === m ? "border-mm-gold bg-mm-gold/10 text-mm-navy" : "border-mm-navy/15 text-mm-navy/70 hover:border-mm-steel"}`}
                    data-testid={`grant-months-${m}`}
                  >
                    {m}
                  </button>
                ))}
                <button
                  onClick={() => setGrantMonths(0)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-bold border-2 ${grantMonths === 0 ? "border-mm-gold bg-mm-gold/10 text-mm-navy" : "border-mm-navy/15 text-mm-navy/70 hover:border-mm-steel"}`}
                  data-testid="grant-months-indefinite"
                >
                  Indefinite
                </button>
              </div>
              <input
                type="number"
                min={0}
                max={120}
                value={grantMonths}
                onChange={(e) => setGrantMonths(parseInt(e.target.value) || 0)}
                className="mt-3 w-full border-2 border-mm-navy/15 rounded-lg px-3 py-2 text-mm-navy"
                data-testid="grant-months-input"
              />
              <div className="text-xs text-mm-navy/60 mt-1">
                {grantMonths === 0 ? "Never expires — admin can revoke later." : `Ends around ${new Date(Date.now() + grantMonths * 30 * 86400 * 1000).toLocaleDateString()}.`}
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setGrantDialog(null)} disabled={grantBusy} className="btn-trail btn-outline-navy text-sm flex-1">Cancel</button>
              <button onClick={submitGrant} disabled={grantBusy} className="btn-trail btn-gold text-sm flex-1 disabled:opacity-50" data-testid="admin-grant-submit">
                {grantBusy ? "Granting..." : "Grant access"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove-user confirmation modal */}
      {confirmRemove && (
        <div
          className="fixed inset-0 z-50 bg-mm-navy/60 backdrop-blur-sm flex items-center justify-center px-4"
          data-testid="remove-user-modal"
          onClick={() => !removing && setConfirmRemove(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6 border-2 border-red-200 fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 text-red-700 flex items-center justify-center shrink-0">
                <Trash2 size={20} strokeWidth={2.5} />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest font-bold text-red-700">Permanent action</div>
                <h3 className="font-heading text-xl font-black text-mm-navy mt-1">
                  Remove {confirmRemove.name || "this user"}?
                </h3>
                <p className="text-sm text-mm-navy/70 mt-2">
                  This deletes <strong className="font-mono">{confirmRemove.email}</strong> and all of their data —
                  climbers ({confirmRemove.student_count || 0}), expeditions ({confirmRemove.expedition_count || 0}),
                  payment history, and password reset tokens.
                  {confirmRemove.stripe_subscription_id && (
                    <> Their active Stripe subscription will also be canceled.</>
                  )}
                </p>
                <p className="text-xs text-mm-navy/60 mt-2">This cannot be undone.</p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setConfirmRemove(null)}
                disabled={removing}
                className="btn-trail btn-outline-navy text-sm"
                data-testid="remove-user-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={removeUser}
                disabled={removing}
                className="btn-trail text-sm bg-red-600 hover:bg-red-700 text-white border-red-600"
                data-testid="remove-user-confirm-btn"
              >
                {removing ? "Removing…" : "Yes, remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatBox = ({ label, value, accent }) => (
  <div className={`p-5 rounded-2xl border ${accent ? "bg-mm-navy text-white border-mm-navy" : "bg-white border-mm-navy/10"}`}>
    <div className={`text-[10px] uppercase tracking-widest font-bold ${accent ? "text-mm-gold" : "text-mm-steel"}`}>{label}</div>
    <div className={`font-heading text-3xl font-black mt-1 ${accent ? "text-white" : "text-mm-navy"}`}>{value}</div>
  </div>
);

const PurgeTestAccountsPanel = ({ onPurged }) => {
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState(null); // {would_delete, kept, sample, current_admin_count}
  const [confirming, setConfirming] = useState(false);
  const [extraPatterns, setExtraPatterns] = useState("");

  const runDryRun = async () => {
    setRunning(true);
    try {
      const extras = extraPatterns.split(",").map((s) => s.trim()).filter(Boolean);
      const r = await api.post("/admin/purge-test-accounts", {
        dry_run: true,
        extra_email_patterns: extras.length ? extras : null,
      });
      setPreview(r.data);
      if (r.data.would_delete === 0) {
        toast.info("No test accounts matched. Nothing to purge.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not scan for test accounts");
    } finally {
      setRunning(false);
    }
  };

  const doPurge = async () => {
    setRunning(true);
    try {
      const extras = extraPatterns.split(",").map((s) => s.trim()).filter(Boolean);
      const r = await api.post("/admin/purge-test-accounts", {
        dry_run: false,
        extra_email_patterns: extras.length ? extras : null,
      });
      const c = r.data.counts || {};
      toast.success(
        `Purged ${r.data.purged_count} accounts. Cleaned ${c.students_deleted || 0} climbers, ${c.expeditions_deleted || 0} expeditions, ${c.email_log_deleted || 0} email log rows.`
      );
      setPreview(null);
      setConfirming(false);
      onPurged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Purge failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="mt-8 bg-white border-2 border-red-200 rounded-2xl p-5" data-testid="purge-test-accounts-panel">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-red-700">Cleanup</div>
          <h2 className="font-heading text-xl font-black text-mm-navy mt-0.5">Purge test accounts</h2>
          <p className="text-sm text-mm-navy/70 mt-1 max-w-2xl">
            Hard-deletes accounts whose email matches automated-test patterns
            (<code className="font-mono text-xs">@example.com</code>, <code className="font-mono text-xs">test_*</code>, <code className="font-mono text-xs">smoke_*</code>, <code className="font-mono text-xs">iter*</code>, etc.)
            and cascades through climbers, expeditions, payments, password resets, and email log. Cancels any active Stripe subscription on each. Never deletes your own admin account or the last admin.
          </p>
        </div>
        <button
          onClick={runDryRun}
          disabled={running}
          className="btn-trail btn-outline-navy text-sm shrink-0"
          data-testid="purge-dry-run-btn"
        >
          {running && !preview ? "Scanning…" : preview ? "Re-scan" : "Scan for test accounts"}
        </button>
      </div>

      <details className="mt-3">
        <summary className="text-xs uppercase tracking-widest font-bold text-mm-steel cursor-pointer hover:text-mm-navy">
          Add extra patterns (advanced)
        </summary>
        <div className="mt-2">
          <input
            type="text"
            value={extraPatterns}
            onChange={(e) => setExtraPatterns(e.target.value)}
            placeholder='Comma-separated regex, e.g. "^myteam_, @internal\\.dev$"'
            className="w-full bg-white border-2 border-mm-navy/15 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-mm-steel"
            data-testid="purge-extra-patterns-input"
          />
          <div className="text-[11px] text-mm-navy/55 mt-1">Optional. Patterns added here apply in addition to the built-in test patterns.</div>
        </div>
      </details>

      {preview && (
        <div className="mt-4 bg-mm-parchment border border-mm-navy/10 rounded-xl p-4" data-testid="purge-preview-block">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">Scan result</div>
              <div className="font-heading text-2xl font-black text-mm-navy mt-1">
                <span data-testid="purge-would-delete-count">{preview.would_delete}</span> test account{preview.would_delete === 1 ? "" : "s"} matched
              </div>
              <div className="text-xs text-mm-navy/60 mt-1">
                Will keep {preview.kept} accounts. Current admin count: {preview.current_admin_count}.
              </div>
            </div>
            {preview.would_delete > 0 && (
              <button
                onClick={() => setConfirming(true)}
                disabled={running}
                className="btn-trail text-sm bg-red-600 hover:bg-red-700 text-white border-red-600"
                data-testid="purge-confirm-open-btn"
              >
                <Trash2 size={14} strokeWidth={2.5} /> Purge {preview.would_delete} account{preview.would_delete === 1 ? "" : "s"}
              </button>
            )}
          </div>
          {!!preview.sample?.length && (
            <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-mm-navy/80" data-testid="purge-sample-list">
              {preview.sample.map((u) => (
                <li key={u.id} className="font-mono truncate">
                  {u.email}
                  {u.is_admin && <span className="ml-1 text-red-700 font-sans font-bold">[admin]</span>}
                </li>
              ))}
              {preview.would_delete > preview.sample.length && (
                <li className="text-mm-navy/50 italic font-sans">…and {preview.would_delete - preview.sample.length} more</li>
              )}
            </ul>
          )}
        </div>
      )}

      {confirming && preview && (
        <div className="fixed inset-0 z-50 bg-mm-navy/70 backdrop-blur-sm flex items-center justify-center px-4" data-testid="purge-confirm-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 border-2 border-red-300 fade-up">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 text-red-700 flex items-center justify-center shrink-0">
                <Trash2 size={20} strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-red-700">Permanent action</div>
                <h3 className="font-heading text-xl font-black text-mm-navy mt-1">
                  Hard-delete <span data-testid="purge-confirm-count">{preview.would_delete}</span> test account{preview.would_delete === 1 ? "" : "s"}?
                </h3>
                <p className="text-sm text-mm-navy/70 mt-2">
                  All climbers, expeditions, payment history, password resets, email log entries, and broadcasts owned by these accounts will be removed. Stripe subscriptions are canceled. This cannot be undone.
                </p>
                <p className="text-xs text-mm-navy/60 mt-2 font-bold">Your own admin account is never affected.</p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setConfirming(false)}
                disabled={running}
                className="btn-trail btn-outline-navy text-sm"
                data-testid="purge-confirm-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={doPurge}
                disabled={running}
                className="btn-trail text-sm bg-red-600 hover:bg-red-700 text-white border-red-600"
                data-testid="purge-confirm-run-btn"
              >
                {running ? "Purging…" : <>Yes, purge now <Trash2 size={14} strokeWidth={2.5} /></>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ViewAsBtn = ({ children, active, onClick, disabled, testid }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`btn-trail text-xs ${active ? "btn-gold" : "btn-outline-navy"}`}
    data-testid={testid}
  >
    {children}
  </button>
);

const PlanPill = ({ plan }) => {
  const map = {
    premium: "bg-mm-gold/25 text-mm-navy",
    trial: "bg-mm-steel/20 text-mm-navy",
    free: "bg-mm-navy/10 text-mm-navy",
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-widest ${map[plan] || map.free}`}>{plan}</span>;
};

const Th = ({ children }) => <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold text-mm-steel">{children}</th>;
const Td = ({ children, colSpan, className = "" }) => <td colSpan={colSpan} className={`px-4 py-3 align-top ${className}`}>{children}</td>;

const ActivityList = ({ title, items }) => (
  <div className="bg-white border border-mm-navy/10 rounded-2xl p-5">
    <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">{title}</div>
    <div className="mt-3 space-y-3">
      {items.length === 0 && <div className="text-mm-navy/60 text-sm">Nothing yet.</div>}
      {items.map((it) => (
        <div key={it.key} className="flex items-start justify-between gap-3 text-sm border-b border-mm-navy/10 pb-2 last:border-b-0 last:pb-0">
          <div>
            <div className="font-bold text-mm-navy">{it.primary}</div>
            <div className="text-xs text-mm-navy/60">{it.secondary}</div>
            <div className="text-[10px] uppercase tracking-widest text-mm-steel font-bold mt-0.5">{it.meta}</div>
          </div>
          <div className="text-[10px] text-mm-navy/50 shrink-0">{it.when?.slice(0, 16)?.replace("T", " ")}</div>
        </div>
      ))}
    </div>
    <ArrowUpRight size={14} className="text-mm-navy/0" />
  </div>
);

export default Admin;
